import {
	ActionListItem,
	BusyIndicator,
	ChangeTriggerKind,
	DropOverActionList,
	Icon,
	IconButton
} from "client/Components";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useRef,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { DestinationWindow } from "client/Api/Apps/common";
import { Guid } from "client_legacy/ndaw/guid";
import { navigateTo } from "client_legacy/ndaw/navigator";
import { ListItem } from "../ListItem";
import { LiveTileContent } from "../LiveTileContent";
import { TileRefresh } from "../TileRefresh/TileRefresh";
import { InterestedZenyaDocParty, ZenyaDocLiveInformationProvider } from "./provider";
import {
	addFavorite,
	ItemsResultStatus,
	search,
	ZenyaDocContentItem,
	ZenyaDocPortal,
	ZenyaDocRecentItemsResponseModel
} from "client/Api/Workspace/LiveInformation/ZenyaDoc/zenyaDocLiveInformation";
import styles from "./BrowseDocuments.module.less";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { AlertManager, useAlerts, useUnmountedCancellation } from "client/Hooks";
import classNames from "classnames";
import { ensureNotNil } from "client/Misc/utils";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { ModelPath } from "client/Validation/path";
import { InvalidInputValidationResult } from "client/Validation/input";
import { tryParseServerErrors } from "client/Validation/serverErrors";

export type BrowseDocumentsProps = {
	tabId: Guid;
	connectionId: number;
	destinationWindow: DestinationWindow;
	provider: ZenyaDocLiveInformationProvider;
	addAnalyticsEvent(): void;
};

export const BrowseDocuments = (props: BrowseDocumentsProps): ReactElement => {
	const {
		tabId,
		connectionId,
		destinationWindow,
		provider,
		addAnalyticsEvent
	} = props;

	const [searchTerm, setSearchTerm] = useState<string>("");

	const [portals, setPortals] = useState<readonly ZenyaDocPortal[]>([]);

	const [isPortalSelectorOpen, setIsPortalSelectorOpen] = useState<boolean>(false);
	const [activePortalId, setActivePortalId] = useState<number | null>(null);
	const portalSelectorRef: RefObject<HTMLButtonElement | null> = useRef(null);

	useEffect(
		() => {
			async function fetch(connectionId: number, cancellation: AbortSignal): Promise<void> {
				setPortals([]);
				setActivePortalId(null);

				try {
					const portals: readonly ZenyaDocPortal[] = await provider.listPortals(connectionId);
					if (cancellation.aborted) {
						return;
					}
					setPortals(portals);

				} catch (e) {
					console.error("Failed to load the portals", e);
				}
			}

			const cancellation = new AbortController();
			fetch(connectionId, cancellation.signal);
			return () => cancellation.abort();
		},
		[connectionId, provider]
	);

	const portalOptions: (ZenyaDocPortal | { id: null; name: string; })[] = [...portals];
	portalOptions.unshift({
		id: null,
		name: localize("_AllPortals")
	});

	const activePortal: (ZenyaDocPortal | { id: null; name: string; }) = ensureNotNil(
		portalOptions.find(x => x.id === activePortalId)
	);

	const renderView = (): ReactElement => {
		if (searchTerm) {
			return (
				<SearchResults
					connectionId={connectionId}
					portalId={activePortalId}
					searchTerm={searchTerm}
					destinationWindow={destinationWindow}
					onListItemClick={addAnalyticsEvent}
				/>
			);
		}

		return (
			<RecentlyOpenedResults
				tabId={tabId}
				connectionId={connectionId}
				destinationWindow={destinationWindow}
				provider={provider}
				onListItemClick={addAnalyticsEvent}
			/>
		);
	};

	return (
		<>
			<div className={styles.searchContainer}>
				<div className={styles.search}>
					<LiveTileContent.SearchInput
						onChange={setSearchTerm}
						placeholder={localize("_SearchInName").replace("{{Name}}", activePortal.name)}
						trigger={{ kind: ChangeTriggerKind.Debounced }}
					/>
				</div>
				<div className={styles.portalSelector}>
					<IconButton
						kind="transparent"
						icon="filter"
						onClick={() => setIsPortalSelectorOpen(true)}
						ref={portalSelectorRef}
						trackingId="filter"
					/>
					{(isPortalSelectorOpen && portalSelectorRef.current) && (
						<DropOverActionList
							target={portalSelectorRef.current}
							targetAlignment="br"
							contentAlignment="tr"
							onClose={() => setIsPortalSelectorOpen(false)}
							isHeightConstrainted
							menuTitle={localize("_FilterBy")}
						>
							{portalOptions.map((x) => {
								const isSelected: boolean = x.id === activePortal.id;
								return (
									<ActionListItem
										isActive={isSelected}
										rightIcon={isSelected ? "check" : undefined}
										key={x.id ?? "__ALL__"}
										onClick={() => setActivePortalId(x.id)}
										text={x.name}
									/>
								);
							})}
						</DropOverActionList>
					)}
				</div>
			</div>
			{renderView()}
		</>
	);
};
BrowseDocuments.displayName = "BrowseDocuments";

type RecentlyOpenedLoadingState = {
	isLoading: boolean;
	hasTooManyConsecutiveErrors: boolean;
	hasGlobalFailure: boolean;
	response: ZenyaDocRecentItemsResponseModel | undefined;
};

type RecentlyOpenedResultsProps = {
	tabId: Guid;
	connectionId: number;
	provider: ZenyaDocLiveInformationProvider;
	destinationWindow: DestinationWindow;
	onListItemClick(): void;
};

const RecentlyOpenedResults = (props: RecentlyOpenedResultsProps): ReactElement => {
	const {
		tabId,
		connectionId,
		provider,
		destinationWindow,
		onListItemClick
	} = props;

	const [loadingState, setLoadingState] = useState<RecentlyOpenedLoadingState>({
		isLoading: true,
		hasGlobalFailure: false,
		hasTooManyConsecutiveErrors: false,
		response: undefined
	});

	useEffect(
		() => {
			const party: InterestedZenyaDocParty = {
				partyId: tabId,
				resourceId: {
					connectionId
				},
				informAboutGlobalRetrievalFailure: (): void => {
					setLoadingState((x) => ({
						...x,
						isLoading: false,
						hasTooManyConsecutiveErrors: false,
						hasGlobalFailure: true,
						reponse: undefined
					}));
				},
				informAboutConsecutiveErrors: (): void => {
					setLoadingState((x) => ({
						...x,
						isLoading: false,
						hasTooManyConsecutiveErrors: true
					}));
				},
				updateRecentItems: (response: ZenyaDocRecentItemsResponseModel): void => {
					setLoadingState((x) => ({
						...x,
						isLoading: false,
						hasTooManyConsecutiveErrors: false,
						hasGlobalFailure: false,
						response
					}));
				}
			};

			setLoadingState((x) => ({
				...x,
				isLoading: true
			}));

			provider.register(party, { recentItems: true});

			return () => {
				provider.unregister(party);
			};
		},
		[
			connectionId,
			provider,
			tabId
		]
	);

	const refreshHandler = (): void => {
		setLoadingState((x) => ({
			...x,
			isLoading: true
		}));
		provider.forceReload();
	};

	const {
		isLoading,
		hasTooManyConsecutiveErrors,
		hasGlobalFailure,
		response
	} = loadingState;

	const { isAdmin } = userConfiguration();

	const renderContent = (): ReactElement | null => {

		if (hasTooManyConsecutiveErrors) {
			return <TileRefresh onRefresh={refreshHandler} />;
		}

		if (hasGlobalFailure || (response && response.status === ItemsResultStatus.GeneralFailure)) {
			return <LiveTileContent.Message text={localize("_UnableToRetrieveItems")} />;
		}

		if (!response) {
			return null;
		}

		if (response.status === ItemsResultStatus.EmailAddressRequired) {
			return (
				<LiveTileContent.Message
					text={localize(isAdmin
						? "_ZenyaEmailRequiredToActivateMessageForAdmin"
						: "_ZenyaEmailRequiredToActivateMessage"
					)}
				/>
			);
		}

		if (response.page.items.length === 0) {
			return <LiveTileContent.Message text={localize("_YouDontHaveAnyRecentDocuments")} />;
		}

		return (
			<LiveTileContent.List>
				{ response.page.items.map((x) => (
					<ContentItemListItem
						key={x.id}
						connectionId={connectionId}
						contentItem={x}
						destinationWindow={destinationWindow}
						onClick={onListItemClick}
					/>
				))}
			</LiveTileContent.List>
		);
	};

	return (
		<LiveTileContent.BusyIndicatorContainer>
			{ isLoading && (
				<BusyIndicator />
			)}
			<LiveTileContent.Scrollable>
				<LiveTileContent.ContentHeader text={localize("_RecentlyOpened")} />

				{renderContent()}
			</LiveTileContent.Scrollable>
		</LiveTileContent.BusyIndicatorContainer>
	);
};
RecentlyOpenedResults.displayName = "RecentlyOpenedResults";

type SearchLoadingState = {
	isLoading: boolean;
	hasError: boolean;
	hasMore: boolean;
	errorMessage?: string;
	items: readonly ZenyaDocContentItem[];
};

type SearchResultsProps = {
	connectionId: number;
	portalId: number | null;
	searchTerm: string;
	destinationWindow: DestinationWindow;
	onListItemClick(): void;
};

const SearchResults = (props: SearchResultsProps): ReactElement => {
	const {
		connectionId,
		portalId,
		searchTerm,
		destinationWindow,
		onListItemClick
	} = props;

	const [pageNumber, setPageNumber] = useState<number>(0);

	const [loadingState, setLoadingState] = useState<SearchLoadingState>({
		isLoading: true,
		hasError: false,
		hasMore: false,
		errorMessage: "",
		items: []
	});

	useEffect(
		() => {
			setPageNumber(0);
		},
		[portalId, connectionId, searchTerm]
	);

	useEffect(
		() => {
			async function fetch(
				connectionId: number,
				portalId: number | null,
				searchTerm: string,
				pageNumber: number,
				signal: AbortSignal
			): Promise<void> {
				setLoadingState(x => {
					const copy: SearchLoadingState = {
						...x,
						hasMore: false,
						hasError: false,
						isLoading: true,
						errorMessage: ""
					};

					if (pageNumber === 0) {
						copy.items = [];
					}

					return copy;
				});

				try {
					const page: PageResult<ZenyaDocContentItem> = await search(
						connectionId,
						portalId,
						searchTerm,
						pageNumber,
						signal
					);

					setLoadingState(x => {
						const items: readonly ZenyaDocContentItem[] = [...x.items, ...page.items];
						const hasMore: boolean = items.length < page.totalCount;

						return {
							...x,
							isLoading: false,
							items,
							hasMore
						};
					});
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					const result: TryResult<ReadonlyMap<ModelPath, InvalidInputValidationResult>> = tryParseServerErrors(e);
					let errorMessage: string | undefined;
					if (result.wasSuccessful && result.result.has(ModelPath.root)) {
						errorMessage = result.result.get(ModelPath.root)!.errorMessage;
					}

					setLoadingState(x => ({
						...x,
						isLoading: false,
						hasError: true,
						errorMessage
					}));
				}
			}

			const cancellation = new AbortController();

			// The additional timeout exist here because when e.g. the search term changes while you are on page "1",
			// it is reset to page "0", however because this effect both depends on search term and page, the 1st fetch
			// will be aborted near instantly. With the setTimeout, we delay the actually fetching till all effects have
			// ran
			setTimeout(
				() => {
					const signal: AbortSignal = cancellation.signal;
					if (signal.aborted) {
						return;
					}

					fetch(
						connectionId,
						portalId,
						searchTerm,
						pageNumber,
						signal
					);
				},
				1
			);

			return () => cancellation.abort();
		},
		[portalId, connectionId, searchTerm, pageNumber]
	);

	const loadMoreHandler = () => {
		setPageNumber(pageNumber + 1);
	};

	const {
		isLoading,
		hasError,
		hasMore,
		items,
		errorMessage
	} = loadingState;

	const renderContent = (): ReactElement => {
		if (hasError) {
			return (
				<LiveTileContent.Message
					text={errorMessage
						? errorMessage
						: localize("_UnableToRetrieveItems")
					}
				/>
			);
		}

		if (!isLoading && items.length === 0) {
			return <LiveTileContent.Message text={localize("_NoResultsFound")} />;
		}

		return (
			<>
				<LiveTileContent.List>
					{items.map((x) => (
						<ContentItemListItem
							key={x.id}
							connectionId={connectionId}
							contentItem={x}
							destinationWindow={destinationWindow}
							onClick={onListItemClick}
						/>
					))}
				</LiveTileContent.List>
				{hasMore && (
					<LiveTileContent.LoadMoreButton onClick={loadMoreHandler} />
				)}
			</>
		);
	};

	return (
		<LiveTileContent.BusyIndicatorContainer>
			{isLoading && (
				<BusyIndicator />
			)}
			<LiveTileContent.Scrollable>

				{renderContent()}
			</LiveTileContent.Scrollable>
		</LiveTileContent.BusyIndicatorContainer>
	);
};
SearchResults.displayName = "SearchResults";

type ContentItemListItemProps = {
	connectionId: number;
	contentItem: ZenyaDocContentItem;
	destinationWindow: DestinationWindow;
	onClick(): void;
};

const ContentItemListItem = (props: ContentItemListItemProps): ReactElement => {
	const {
		connectionId,
		destinationWindow,
		contentItem,
		onClick
	} = props;

	const [isActionsOpen, setIsActionsOpen] = useState<boolean>();
	const moreRef: RefObject<HTMLDivElement | null> = useRef(null);
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const alertManager: AlertManager = useAlerts();

	const moreHandler = (): void => {
		onClick();
		setIsActionsOpen(true);
	};

	const addToFavoriteHandler = async (): Promise<void> => {
		setIsActionsOpen(false);

		try {
			await addFavorite(
				connectionId,
				contentItem.id,
				contentItem.name,
				globalCancellation
			);
			alertManager.showSuccess(localize("_SuccessfullyAddedTheItemToFavorites"));
		} catch (e) {
			if (isCancelledError(e)) {
				return;
			}

			console.error(e);
			alertManager.showError(localize("_GeneralErrorMessage"));
		}
	};

	const clickHandler = useCallback(
		(): void => {
			onClick();
			navigateTo(contentItem.url, destinationWindow === DestinationWindow.NewWindow);
		},
		[
			contentItem.url,
			destinationWindow,
			onClick
		]
	);

	return (
		<ListItem
			title={contentItem.name}
			isHoverable
			hasSeparateClickableAreas
			blockClassName={styles.listItem}
		>
			<ListItem.ClickableArea
				position="first"
				shouldGrow
				onClick={clickHandler}
			>
				<ListItem.Content>
					<ListItem.Row>
						<ListItem.Title>
							{contentItem.name}
						</ListItem.Title>
					</ListItem.Row>
					<ListItem.Row>
						<ListItem.Info>
							{contentItem.type}
						</ListItem.Info>
					</ListItem.Row>
				</ListItem.Content>
			</ListItem.ClickableArea>

			<ListItem.ClickableArea
				position="last"
				onClick={moreHandler}
			>
				<div
					className={classNames(
						styles.moreIcon,
						{
							[styles.moreIcon_isOpen]: isActionsOpen
						}
					)}
					title={localize("_More")}
					ref={moreRef}
				>
					<Icon value="ellipsis" />
				</div>
			</ListItem.ClickableArea>

			{(isActionsOpen && moreRef.current) && (
				<DropOverActionList
					target={moreRef.current}
					onClose={() => setIsActionsOpen(false)}
					menuTitle={contentItem.name}
				>
					<ActionListItem
						text={localize("_AddToFavorites")}
						icon="star-filled"
						onClick={addToFavoriteHandler}
					/>
				</DropOverActionList>
			)}
		</ListItem>
	);
};
ContentItemListItem.displayName = "ContentItemListItem";